import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { OPTOMETRIST_QUESTIONS, DISPENSER_QUESTIONS } from "../data/questions";

const API = window.__RUNTIME_CONFIG__?.apiBase || window.location.origin;

export default function CandidateTest({ token: tokenProp }) {
  const { candidateId } = useParams();
  const token = tokenProp || candidateId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [role, setRole] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/test/${token}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) {
          if (d.status === "submitted" || d.status === "marked") {
            setAlreadyDone(true);
          } else {
            setError(d.error);
          }
        } else {
          setRole(d.role);
          setQuestions(d.role === "optometrist" ? OPTOMETRIST_QUESTIONS : DISPENSER_QUESTIONS);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not connect to the server. Please try again later.");
        setLoading(false);
      });
  }, [token]);

  const handleAnswer = (e) => {
    setAnswers({ ...answers, [current]: e.target.value });
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter((q) => !(answers[q.index] || "").trim());
    if (unanswered.length > 0) {
      if (!window.confirm(`You have ${unanswered.length} unanswered question(s). Submit anyway?`)) return;
    }
    setSubmitting(true);
    try {
      const payload = questions.map((q) => ({
        question_index: q.index,
        answer_text: (answers[q.index] || "").trim(),
      }));
      const r = await fetch(`${API}/api/test/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: payload }),
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.error || "Submission failed");
      } else {
        setSubmitted(true);
      }
    } catch {
      setError("Could not connect to the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    background: "#f0f4f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    fontFamily: "system-ui, sans-serif",
  };

  const cardStyle = {
    background: "white",
    borderRadius: 16,
    padding: 40,
    maxWidth: 680,
    width: "100%",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
  };

  if (loading) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <p style={{ textAlign: "center", color: "#6b7280" }}>Loading your assessment...</p>
      </div>
    </div>
  );

  if (alreadyDone) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 56 }}>✅</div>
          <h2 style={{ color: "#166534", marginTop: 16 }}>Assessment Already Submitted</h2>
          <p style={{ color: "#6b7280" }}>Your assessment has already been received. Thank you!</p>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 56 }}>❌</div>
          <h2 style={{ color: "#991b1b", marginTop: 16 }}>Assessment Unavailable</h2>
          <p style={{ color: "#6b7280" }}>{error}</p>
        </div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 56 }}>🎉</div>
          <h2 style={{ color: "#166534", marginTop: 16 }}>Assessment Submitted!</h2>
          <p style={{ color: "#6b7280", marginTop: 8 }}>
            Thank you for completing your assessment. The recruitment team will be in touch with you soon.
          </p>
        </div>
      </div>
    </div>
  );

  const q = questions[current];
  const progress = Math.round(((current + 1) / questions.length) * 100);
  const answeredCount = questions.filter((x) => (answers[x.index] || "").trim()).length;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{
                background: "#8b3db8",
                color: "white",
                borderRadius: 20,
                padding: "4px 12px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "capitalize",
              }}>
                {role === "optometrist" ? "Optometrist Assessment" : "Optical Dispenser Assessment"}
              </span>
            </div>
            <span style={{ color: "#6b7280", fontSize: 13 }}>
              {answeredCount} / {questions.length} answered
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: 16, background: "#e5e7eb", borderRadius: 8, height: 8 }}>
            <div style={{ width: `${progress}%`, background: "#8b3db8", borderRadius: 8, height: 8, transition: "width 0.3s" }} />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af", textAlign: "right" }}>
            Question {current + 1} of {questions.length}
          </div>
        </div>

        {/* Question */}
        <div style={{ marginBottom: 24 }}>
          <div style={{
            background: "#f5f0fb",
            borderLeft: "4px solid #8b3db8",
            padding: "16px 20px",
            borderRadius: "0 8px 8px 0",
            marginBottom: 16,
          }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 16, color: "#1f2937", lineHeight: 1.6 }}>
              {q.question}
            </p>
          </div>

          <textarea
            value={answers[q.index] || ""}
            onChange={handleAnswer}
            placeholder="Type your answer here..."
            rows={7}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 15,
              resize: "vertical",
              fontFamily: "inherit",
              boxSizing: "border-box",
              outline: "none",
            }}
          />
        </div>

        {/* Navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={current === 0}
            style={{
              padding: "10px 22px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: current === 0 ? "#f9fafb" : "white",
              color: current === 0 ? "#9ca3af" : "#374151",
              cursor: current === 0 ? "default" : "pointer",
              fontWeight: 600,
            }}
          >
            ← Previous
          </button>

          {/* Question dots */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", flex: 1 }}>
            {questions.map((x, i) => (
              <div
                key={x.index}
                onClick={() => setCurrent(i)}
                title={`Question ${i + 1}`}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: i === current ? "#8b3db8" : (answers[x.index] || "").trim() ? "#a3e635" : "#e5e7eb",
                  cursor: "pointer",
                  border: i === current ? "2px solid #6b21a8" : "2px solid transparent",
                }}
              />
            ))}
          </div>

          {current < questions.length - 1 ? (
            <button
              type="button"
              onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: "none",
                background: "#8b3db8",
                color: "white",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: "none",
                background: submitting ? "#9ca3af" : "#166534",
                color: "white",
                cursor: submitting ? "default" : "pointer",
                fontWeight: 700,
              }}
            >
              {submitting ? "Submitting..." : "Submit Assessment"}
            </button>
          )}
        </div>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: "#9ca3af" }}>
          You can navigate between questions using the arrows or the dots. Your answers are saved as you type.
        </p>
      </div>
    </div>
  );
}
